import {
  StockResponseType,
  Stock,
  InfoClient,
  updateStock,
  validateStockExchange,
  point_of_sale,
  updatePointOfSale,
  searchStock,
} from "_api";
import {
  ClientInformations,
  Modal,
  PageTitle,
  PointOfSaleModalStock,
  StockExchangeSharing,
  StockTable,
} from "_components";
import { useLoading, useModal } from "_hooks";
import { AxiosError } from "axios";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./stock-page.scss";
import { atomDealers, atomDealersBrand } from "_atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import { atomUser } from "_atoms";
import Select, { components, InputProps, GroupBase, StylesConfig } from "react-select";

const customStyles: StylesConfig = {
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const PAGINATION_STEP = 10000;

const StockPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const { setModal } = useModal();
  const { loading, setLoading } = useLoading();

  const [stocks, setStocks] = useState<Stock[]>([]);
  const [dealers] = useRecoilState(atomDealers);
  const [dealersBrand] = useRecoilState(atomDealersBrand);
  const user = useRecoilValue(atomUser);

  // === FILTER STATE ===
  const FILTER_COLUMNS = [
    { value: "ORNO", label: t("order_number") },
    { value: "millesime", label: t("year") },
    { value: "brand", label: t("brand") },
    { value: "model", label: t("model") },
    { value: "point_of_sale", label: t("dealer_name") },
    { value: "billingDate", label: t("billing_date") },
  ];

  const [activeFilters, setActiveFilters] = useState<Array<{ column?: string; value: string }>>([]);
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<{ label: string; value: string } | null>(null);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{ label: string; value: string } | null>(null); // ✅ Nouvel état pour brand
  const [noDataFound, setNoDataFound] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCUNO, setSelectedCUNO] = useState<string>("");
  const [isStockModal, setIsStockModal] = useState(false);
  const [isClientInfoModal, SetIsClientInfoModal] = useState(false);
  const [isPointOfSaleModal, setIsPointOfSaleModal] = useState(false);
  const [currentORNO, setCurrentORNO] = useState<string | null>(null);
  const [currentPointOfSaleId, setCurrentPointOfSaleId] = useState<string | null>(null);
  const [stockExchangeCommentary, setStockExchangeCommentary] = useState<string | null>(null);

  // === PAGINATION STATE ===
  const [pagination, setPagination] = useState({ skip: 0, top: PAGINATION_STEP });
  const [hasMore, setHasMore] = useState(true);

  // === BRAND OPTIONS DYNAMIQUES ===
  const availableBrands = useMemo(() => {
    const pointOfSaleFilter = activeFilters.find(f => f.column === "point_of_sale");
    let filteredDealers = dealersBrand;

    if (pointOfSaleFilter) {
      filteredDealers = dealersBrand.filter(d => d.value === pointOfSaleFilter.value);
    }

    const brandMap = new Map<number, { id: number; name: string }>();
    if (filteredDealers && filteredDealers.length > 0) {
      filteredDealers.forEach(dealer => {
        if (dealer && dealer.brands) {
          dealer.brands.forEach(brand => {
            if (!brandMap.has(brand.id)) {
              brandMap.set(brand.id, { id: brand.id, name: brand.name });
            }
          });
        }
      });
    }

    return Array.from(brandMap.values()).map(brand => ({
      label: brand.name,
      value: brand.id.toString(),
    }));
  }, [dealersBrand, activeFilters]);

  // === REUSABLE UI HELPERS ===
  const CustomSelectInput: React.FC<
    InputProps<{ label: string; value: string }, false, GroupBase<{ label: string; value: string }>>
  > = (props) => {
    const { onKeyDown } = props;
    return (
      <components.Input
        {...props}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleAddFilter();
          }
          if (onKeyDown) {
            onKeyDown(e);
          }
        }}
      />
    );
  };

  const getSelectedColumnLabel = () => {
    if (!selectedColumn) return t("filter_dropdown_list");
    const column = FILTER_COLUMNS.find((col) => col.value === selectedColumn);
    return column ? t(column.label) : t("filter_dropdown_list");
  };

  // === FILTER LOGIC ===
  const handleSelectFilterColumn = (columnValue: string) => {
    setSelectedColumn(columnValue);
    setSearchTerm("");
    setSelectedOption(null);
    setSelectedBrandOption(null); // ✅ Réinitialiser aussi le brand
  };

  const handleOptionChange = (option: { label: string; value: string } | null) => {
    setSelectedOption(option);
  };

  const handleBrandOptionChange = (option: { label: string; value: string } | null) => {
    setSelectedBrandOption(option);
  };

  const handleAddFilter = () => {
    if (!selectedColumn) return;

    const isReplacing = activeFilters.some((f) => f.column === selectedColumn);
    if (!isReplacing && activeFilters.length >= 5) {
      toast.error(t("maximum_filters_reached"));
      return;
    }

    const otherFilters = activeFilters.filter((f) => f.column !== selectedColumn);
    let newFilter = null;

    if (selectedColumn === "billingDate") {
      if (searchTerm.trim()) {
        newFilter = { column: "billingDate", value: searchTerm.trim() };
      }
    } else if (selectedColumn === "point_of_sale") {
      if (selectedOption) {
        newFilter = { column: "point_of_sale", value: selectedOption.value };
      }
    } else if (selectedColumn === "brand") {
      if (selectedBrandOption) {
        newFilter = { column: "brand", value: selectedBrandOption.value };
      }
    } else {
      // ORNO, millesime, model → free text
      if (searchTerm.trim()) {
        newFilter = { column: selectedColumn, value: searchTerm.trim() };
      }
    }

    if (newFilter) {
      setActiveFilters([...otherFilters, newFilter]);
      setSelectedOption(null);
      setSelectedBrandOption(null); // ✅ Réinitialiser après ajout
      setSearchTerm("");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    const currentColumn = selectedColumn || undefined;
    const isReplacing = activeFilters.some((f) => f.column === currentColumn);
    if (!isReplacing && activeFilters.length >= 5) {
      toast.error(t("maximum_filters_reached"));
      return;
    }
    const otherFilters = activeFilters.filter((f) => f.column !== currentColumn);
    setActiveFilters([...otherFilters, { column: currentColumn, value: searchTerm.trim() }]);
    setSearchTerm("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch(e as unknown as React.FormEvent);
    }
  };

  const removeFilter = (index: number) => {
    const newFilters = [...activeFilters];
    newFilters.splice(index, 1);
    setActiveFilters(newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
  };

  // === SERVER-SIDE FILTER + PAGINATION ===
  const buildBackendFilters = () => {
    const filters: Record<string, string[] | string> = {};

    // 1. Gérer point_of_sale
    const pointOfSaleFilter = activeFilters.find(f => f.column === "point_of_sale");
    if (pointOfSaleFilter) {
      filters.point_of_sale = [pointOfSaleFilter.value];
    } else if (dealersBrand.length > 0) {
      filters.point_of_sale = dealersBrand.map(d => d.value);
    }

    // 2. Gérer billingDate
    /*const billingDateFilter = activeFilters.find(f => f.column === "billingDate");
    if (billingDateFilter) {
      filters.billingDate = billingDateFilter.value;
    }*/

    // 3. Gérer les autres filtres
    activeFilters.forEach((filter) => {
      if (!filter.column) return;
      if (filter.column === "point_of_sale") return;

      if (!filters[filter.column]) {
        filters[filter.column] = [];
      }
      (filters[filter.column] as string[]).push(filter.value);
    });

    return filters;
  };

  const consecutiveEmptyCalls = useRef(0);

  const fetchStock = async (skip: number, merge = false) => {
    if (!user) {
      setNoDataFound(true);
      setHasMore(false);
      return;
    }

    setLoading(true);
    try {
      const filters = buildBackendFilters();
      const res = await searchStock(user.country, filters, skip, PAGINATION_STEP);
      const newStocks = res.data.stock;

      if (newStocks.length > 0) {
        consecutiveEmptyCalls.current = 0; // Reset counter on success
        if (skip === 0) {
          setStocks(newStocks);
          setNoDataFound(false);
        } else if (merge) {
          setStocks((prev) => [...prev, ...newStocks]);
        }
        // Even if we get fewer than PAGINATION_STEP items, we continue because
        // backend filtering might have hidden some items from the full page.
        setHasMore(true);
      } else {
        // No items found in this batch
        consecutiveEmptyCalls.current += 1;
        if (consecutiveEmptyCalls.current < 3) {
          // Retry with next page
          setPagination((p) => ({ ...p, skip: p.skip + PAGINATION_STEP }));
        } else {
          // Stop after 3 empty attempts
          setHasMore(false);
          if (skip === 0 || (stocks.length === 0 && !merge)) {
            setStocks([]);
            setNoDataFound(true);
          }
        }
      }
    } catch (error) {
      toast.error(t("toast_no_more_results"));
      setHasMore(false);
      if (skip === 0) {
        setStocks([]);
        setNoDataFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    consecutiveEmptyCalls.current = 0;
    setPagination({ skip: 0, top: PAGINATION_STEP });
    fetchStock(0, false);
  }, [dealers, user, JSON.stringify(activeFilters)]);

  useEffect(() => {
    if (pagination.skip > 0) {
      fetchStock(pagination.skip, true);
    }
  }, [pagination]);

  // === AUTO-CLEAN BRAND FILTER WHEN POINT OF SALE CHANGES ===
  useEffect(() => {
    const pointOfSaleFilter = activeFilters.find(f => f.column === "point_of_sale");
    const brandFilter = activeFilters.find(f => f.column === "brand");

    if (!brandFilter) return;

    // Récupérer l'ID du brand (sous forme de nombre)
    const brandId = Number(brandFilter.value);
    if (isNaN(brandId)) return;

    // Déterminer quels dealers sont actifs
    let allowedDealers = dealersBrand;
    if (pointOfSaleFilter) {
      // Un seul dealer sélectionné
      const dealer = dealersBrand.find(d => d.value === pointOfSaleFilter.value);
      if (dealer) {
        allowedDealers = [dealer];
      }
    }

    // Vérifier si AU MOINS UN dealer autorisé propose ce brand ID
    const isBrandValid = allowedDealers.some(dealer =>
      dealer.brands.some(b => b.id === brandId)
    );

    if (!isBrandValid) {
      // Supprimer uniquement le filtre brand
      setActiveFilters(prev => prev.filter(f => f.column !== "brand"));
    }
  }, [activeFilters, dealersBrand]);

  // === MODAL HANDLERS (inchangés) ===
  const handleSelectedCUNOChange = (newCUNO: string) => {
    setSelectedCUNO(newCUNO);
  };

  const handleOpenStockModal = (stock: Stock | null) => {
    if (stock) {
      setIsStockModal(true);
      setCurrentPointOfSaleId(stock.point_of_sale.id);
      setCurrentORNO(stock.ORNO);
    } else {
      setIsStockModal(false);
    }
  };

  const handleOpenPointOfSaleModal = (stock: Stock | null) => {
    if (stock) {
      setModal(true);
      setIsPointOfSaleModal(true);
      setCurrentPointOfSaleId(stock.ORNO);
    } else {
      setModal(false);
      setIsPointOfSaleModal(false);
      setCurrentPointOfSaleId(null);
    }
  };

  const handleOpenClientInfoModal = (stock: Stock | null) => {
    if (stock) {
      setModal(true);
      SetIsClientInfoModal(true);
      setCurrentORNO(stock.ORNO);
    } else {
      setModal(false);
      SetIsClientInfoModal(false);
    }
  };

  const handleCancel = (orno: string) => {
    setStocks((prev) =>
      prev.map((stock) => (stock.ORNO === orno ? { ...stock, stockExchange: false } : stock))
    );
  };

  const handleClientInfoChange = (infoClient: InfoClient) => {
    setLoading(true);
    const filteredStock = stocks.find((s) => s.ORNO === currentORNO);
    if (filteredStock && user) {
      updateStock(filteredStock.ORNO, filteredStock.CUNO, infoClient, user, null, false)
        .then(() => {
          fetchStock(pagination.skip, false);
          toast.success(t("client_add_success"));
        })
        .catch(() => {
          setLoading(false);
          toast.error(t("toast_get_data_error"));
        });
    }
  };

  const handleStockExchangeSharingChange = (commentary: string) => {
    setStockExchangeCommentary(commentary);
  };

  const onStockExchangeSharingSubmit = () => {
    const stockFound = stocks.find((s) => s.ORNO === currentORNO);
    if (stockFound && stockExchangeCommentary) {
      setLoading(true);
      validateStockExchange(stockFound, dealers, stockExchangeCommentary, user)
        .then(() => {
          fetchStock(pagination.skip, false);
          toast.success(t("exchange_success"));
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
          toast.error(t("toast_unknown_error"));
        });
    }
  };

  const handleSelectedOptionChange = (point_of_sale: point_of_sale | undefined) => {
    setLoading(true);
    if (point_of_sale && currentPointOfSaleId) {
      const stock = stocks.find((s) => s.ORNO === currentPointOfSaleId);
      if (stock) {
        updatePointOfSale(currentPointOfSaleId, point_of_sale.cunoAdid, point_of_sale)
          .then(() => {
            fetchStock(pagination.skip, false);
            toast.success(t("point_of_sale_update_success"));
          })
          .catch((error: any) => {
            setLoading(false);
            if (error.response) {
              switch (error.response.status) {
                case 412:
                  toast.error(t("toast_error_updating_point_of_sale_on_azure"));
                  break;
                case 413:
                  toast.error(t("toast_error_updating_point_of_sale_on_m3"));
                  break;
                default:
                  toast.error(t("toast_get_data_error"));
              }
            } else {
              toast.error(t("toast_get_data_error"));
            }
          });
      } else {
        setLoading(false);
        toast.error(t("toast_get_data_error"));
      }
    } else {
      setLoading(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 10 && hasMore && !loading) {
      setPagination((p) => ({ ...p, skip: p.skip + PAGINATION_STEP }));
    }
  };

  // === RENDER ===
  const isBillingDateColumn = selectedColumn === "billingDate";

  return (
    <div className="stocks">
      <PageTitle
        title={t("my_vehicle_stock_title")}
        description={t("my_vehicle_stock_description")}
      />
      <div className="stocks__main-wrapper">
        <div className="stocks__actions-wrapper">
          <div className="stocks__search-filter-container">
            <form onSubmit={handleSearch} className="stocks__search-form">
              <div className="stocks__filter" ref={filterDropdownRef}>
                <div className="stocks__filter-button">{getSelectedColumnLabel()}</div>
                <div className="stocks__filter-options">
                  {FILTER_COLUMNS.map((col) => (
                    <button
                      key={col.value}
                      type="button"
                      className="stocks__filter-option"
                      onClick={() => handleSelectFilterColumn(col.value)}
                    >
                      {col.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="stocks__search-input-container">
                {selectedColumn === "billingDate" ? (
                  <input
                    type="date"
                    className="stocks__search-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    disabled={!selectedColumn}
                    placeholder="YYYY-MM-DD"
                  />
                ) : selectedColumn === "point_of_sale" ? (
                  <Select
                    options={dealersBrand.map((d) => ({
                      label: d.label,
                      value: d.value,
                    }))}
                    onChange={handleOptionChange}
                    placeholder={t("search_placeholder")}
                    isClearable
                    styles={customStyles}
                    components={{ Input: CustomSelectInput }}
                    value={selectedOption}
                    isDisabled={!selectedColumn}
                    noOptionsMessage={() => t("pas_de_choix")}
                  />
                ) : selectedColumn === "brand" ? (
                  // ✅ NOUVEAU : Select dynamique pour les brands
                  <Select
                    options={availableBrands}
                    onChange={handleBrandOptionChange}
                    placeholder={t("search_placeholder")}
                    isClearable
                    styles={customStyles}
                    components={{ Input: CustomSelectInput }}
                    value={selectedBrandOption}
                    isDisabled={!selectedColumn || availableBrands.length === 0}
                    noOptionsMessage={() => t("pas_de_choix")}
                  />
                ) : (
                  // Free text input for ORNO, millesime, model
                  <input
                    type="text"
                    className="stocks__search-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyPress}
                    disabled={!selectedColumn}
                    placeholder={!selectedColumn ? t("before_select_option") : t("search_placeholder")}
                  />
                )}
              </div>

              <button
                type="submit"
                className="stocks__search-button"
                onClick={(e) => {
                  e.preventDefault();
                  handleAddFilter();
                }}
              >
                {t("filter_button")}
              </button>
            </form>

            {activeFilters.length > 0 && (
              <div className="stocks__active-filters">
                {activeFilters.map((filter, index) => (
                  <div key={index} className="stocks__filter-tag">
                    <span className="stocks__filter-text">
                      {(() => {
                        const columnDef = FILTER_COLUMNS.find(col => col.value === filter.column);
                        const columnLabel = columnDef ? t(columnDef.label) : filter.column || "";
                        let displayValue = filter.value;

                        if (filter.column === "point_of_sale") {
                          const dealer = dealersBrand.find(d => d.value === filter.value);
                          displayValue = dealer?.label || filter.value;
                        } else if (filter.column === "brand") {
                          const brandId = Number(filter.value);
                          outer: for (const d of dealersBrand) {
                            for (const b of d.brands) {
                              if (b.id === brandId) {
                                displayValue = b.name;
                                break outer;
                              }
                            }
                          }
                        }

                        return `${columnLabel}: ${displayValue}`;
                      })()}
                    </span>
                    <button
                      className="stocks__filter-remove"
                      onClick={() => removeFilter(index)}
                      aria-label={t("remove_filter")}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {activeFilters.length > 1 && (
                  <button className="stocks__clear-filters" onClick={clearAllFilters}>
                    {t("remove_filters")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {noDataFound ? (
          <p className="stocks__no-data">{t("data_not_found")}</p>
        ) : stocks.length > 0 ? (
          <StockTable
            stockProducts={stocks}
            setClientInfoModal={handleOpenClientInfoModal}
            setPointOfSaleModal={handleOpenPointOfSaleModal}
            setStockInfoModal={handleOpenStockModal}
            onSelectedCUNOChange={handleSelectedCUNOChange}
            onScroll={handleScroll}
          />
        ) : null}

        {/* MODALS */}
        {isClientInfoModal && (
          <Modal content oncancel={() => SetIsClientInfoModal(false)} onValidate={() => SetIsClientInfoModal(false)}>
            <ClientInformations
              title={t("add_order_client")}
              name="clientInfo"
              onClose={() => SetIsClientInfoModal(false)}
              onClientChange={handleClientInfoChange}
            />
          </Modal>
        )}

        {isPointOfSaleModal && (
          <Modal content oncancel={() => setIsPointOfSaleModal(false)} onValidate={() => setIsPointOfSaleModal(false)}>
            <PointOfSaleModalStock
              name="PointOfSale"
              onClose={() => setIsPointOfSaleModal(false)}
              selectedCUNO={selectedCUNO}
              onSelectedOptionChange={handleSelectedOptionChange}
            />
          </Modal>
        )}

        {isStockModal && (
          <Modal
            content
            oncancel={() => setIsStockModal(false)}
            onValidate={() => setIsStockModal(false)}
          >
            <StockExchangeSharing
              onSubmit={onStockExchangeSharingSubmit}
              onCancel={() => handleCancel(currentORNO!)}
              onClose={() => setIsStockModal(false)}
              onStockExchangeSharingChange={handleStockExchangeSharingChange}
            />
          </Modal>
        )}
      </div>
    </div>
  );
};

export default StockPage;